import {DotYouClient} from "@homebase-id/js-lib/core";
import {makeAutoObservable, runInAction} from "mobx";
import {
    HomebaseStorage,
    SaveFileConfig,
    SaveOptions
} from "../core/storage/HomebaseStorageTypes";
import {GuideFlowConfig} from "../../system/GuideFlowConfig";
import {SimplifiedStorageProvider} from "../core/storage/SimplifiedStorageProvider";
import {ContactProvider} from "../contact/ContactProvider";
import {humperdink} from "../../components/Sections/Contact/data";
import {normalizeGuid} from "../DataHelpers";
import {CordCutting, LifeActivation} from "../definitions/builtin/BuiltInHealings";
import {getNewId} from "@homebase-id/js-lib/helpers";
import {addDays} from "date-fns";

const fileConfig: SaveFileConfig = {
    dataType: 1033,
    fileType: 1033,
    targetDrive: GuideFlowConfig.DataDrive
}

type RecommendationType = "completed-healing" | "completed-class" | "follow-up";

export interface Recommendation extends HomebaseStorage {
    id: string,
    contactId: string,
    reason: string,
    type: RecommendationType,
    data: string
}

export interface NextStepRecommendation extends HomebaseStorage {
    id: string,
    contactId: string,
    healingId: string,
    classId: string
}

export interface FollowUpRecommendation extends HomebaseStorage {
    /** Unique identifier for this follow-up recommendation */
    id: string;

    /** The contact this recommendation is about */
    contactId: string;

    /** Short subject line for display (e.g., "Healing follow-up") */
    subject: string;

    /** Standardized reason type for programmatic filtering */
    reason: "birthday" | "checkin" | "new-contact" | "class-followup" | "healing-followup";

    /** Optional longer detail (e.g., "Received a healing 5 days ago") */
    detail?: string;

    /** Optional date this follow-up is due/most relevant */
    dueDate?: string; // ISO string

    /** Link back to the progression event that triggered this follow-up */
    eventId?: string;

    /** Precomputed URL for navigation */
    targetUrl?: string;
}


const followUpGroupId: string = "6d09ed82-d3ae-42c1-b5a6-37cfbbe631b8";
const completedHealingGroupId: string = "db9f1434-b82b-4565-a19c-46ef7ae4498f";
const completedClassGroupId: string = "cdaa3ea0-a775-4737-b4b7-b1ed40be260c";

// @notificationHandler(ContactEvent)
export class RecommendationProvider {
    private static instance: RecommendationProvider
    private readonly provider: SimplifiedStorageProvider<Recommendation>;
    private readonly dotYouClient: DotYouClient;
    public StateChanged: number = 0;

    // public static FileConfig: SaveFileConfig = fileConfig;

    constructor(config: SaveFileConfig, dotYouClient: DotYouClient) {
        this.dotYouClient = dotYouClient;
        this.provider = new SimplifiedStorageProvider<Recommendation>(config, dotYouClient);
        makeAutoObservable(this);
    }

    static getInstance(dotYouClient: DotYouClient) {
        if (RecommendationProvider.instance == null) {
            RecommendationProvider.instance = new RecommendationProvider(fileConfig, dotYouClient);
        }
        return RecommendationProvider.instance;
    }

    public async getFollowUps(days: number = 30): Promise<FollowUpRecommendation[]> {
        const recommendations = await this.provider.getAll<Recommendation>({
            groupId: [this.getGroupId("follow-up")],
            includeArchived: false,
            cassieQueryOptions: {
                forceServerCall: true
            }
        });

        // Convert to FollowUpRecommendation[]
        return recommendations.map((recommendation) => {
            try {
                const parsed: FollowUpRecommendation = JSON.parse(recommendation.data);
                return {
                    ...parsed,
                    id: recommendation.id,           // ensure we use the stored id
                    contactId: recommendation.contactId, // keep top-level linkage
                };
            } catch (err) {
                console.error("Failed to parse follow-up recommendation:", recommendation, err);
                return null;
            }
        }).filter((r): r is FollowUpRecommendation => r !== null);
    }

    public async getNextSteps(): Promise<NextStepRecommendation[]> {
        const c = await ContactProvider.getInstance(this.dotYouClient).getAll();
        if (c && c.length > 0) {

            const nr1: NextStepRecommendation = {
                id: "53f84afe-19e8-4e63-a22b-5cdba07e2387",
                contactId: "e9b947bb10f2f338d1b5b2158eafb35e",
                healingId: LifeActivation.id,
                classId: ""
            };

            const nr2: NextStepRecommendation = {
                id: "13c6ec76-663a-4e42-b4eb-00c41f2c8b28",
                contactId: normalizeGuid(humperdink.id),
                healingId: CordCutting.id,
                classId: ""
            };

            return [nr1, nr2];

        }
        return [];
    }

    public async getClassRecommendations(classDefinitionId: string): Promise<Recommendation[]> {

        const results = await this.provider.getAll<Recommendation>({
            groupId: [this.getGroupId("completed-class")],
        });

        return results;
        // Recommendations are based on the following
        // 1. prerequisite classes (the contact has taken the prereq class)
        // 2. recent activity (received a healing; a note was added)
    }

    public async save(recommendation: Recommendation): Promise<any> {

        const options: SaveOptions = {
            groupId: this.getGroupId(recommendation.type),
            tags: []
        }

        await this.provider.saveHeaderFile(recommendation, undefined, options);
    }

    public async saveFollowup(followUp: FollowUpRecommendation): Promise<any> {

        const recommendation: Recommendation = {
            id: followUp.id || getNewId(),
            contactId: followUp.contactId,
            reason: followUp.reason,
            type: "follow-up",
            data: JSON.stringify(followUp)
        }

        const options: SaveOptions = {
            groupId: this.getGroupId(recommendation.type),
            tags: []
        }

        console.log("saving followup", recommendation);
        await this.provider.saveHeaderFile(recommendation, undefined, options);
    }

    public async expire(recommendationId: string): Promise<any> {
        await this.provider.hardDeleteFile(recommendationId);
    }

    public async dismiss(id: string) {
        await this.provider.setArchived(id, true);
    }

    public async undismiss(id: string) {
        await this.provider.setArchived(id, false);
    }

    public async snoozeFollowup(id: string, days: number): Promise<void> {
        // load the followup by id
        const rec = await this.provider.getByUniqueId<Recommendation>(id);
        if (!rec) {
            throw new Error(`Follow-up recommendation with id ${id} not found`);
        }

        let parsed: FollowUpRecommendation;
        try {
            parsed = JSON.parse(rec.data);
        } catch (err) {
            console.error("Failed to parse follow-up recommendation for snooze:", rec, err);
            throw err;
        }

        // calculate new due date
        const current = parsed.dueDate ? new Date(parsed.dueDate) : new Date();
        const newDue = addDays(current, days);

        const updated: FollowUpRecommendation = {
            ...parsed,
            id: rec.id,
            contactId: rec.contactId,
            dueDate: newDue.toISOString(),
            storage: rec.storage
        };

        // console.log("updated followup", updated);
        await this.saveFollowup(updated);

        this.notifyStateChange();
    }

    //

    private getGroupId(type: RecommendationType) {
        switch (type) {
            case "follow-up":
                return followUpGroupId;

            case "completed-healing":
                return completedHealingGroupId;

            case "completed-class":
                return completedClassGroupId;
        }
    }

    private notifyStateChange() {
        runInAction(() => {
            this.StateChanged++;
        });
    }

}